import React, { useState } from "react";
import {
  formatRFC3339,
  startOfDay,
  endOfDay,
  isValid,
  startOfYear,
  endOfYear
} from "date-fns";

import ExportPanel from "./ExportPanel";
import ExportEmailDialog from "./ExportEmailDialog";
import ExportPdfQueuedDialog from "./ExportPdfQueuedDialog";
import config from "../../../config";
import { useApi } from "../../../api";
import { useDevice } from "../../../domain/device";
import { useLocale } from "../../../i18n";

// map each export with the backend export_type and set file format
const EXPORT_TYPES = {
  consumption: { exportType: "simple_consumption", fileFormat: "csv" },
  measurement: { exportType: "extended_consumption", fileFormat: "xlsx" }
};

const ExportButtonContainer = ({ currentView }) => {
  const api = useApi();
  const { device } = useDevice();
  const { locale } = useLocale();

  const [pendingPdfQuery, setPendingPdfQuery] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [pdfQueuedDialogOpen, setPdfQueuedDialogOpen] = useState(false);

  const formatAndEncode = (date, adjustFn) => {
    return encodeURIComponent(formatRFC3339(adjustFn(date)));
  };

  const buildQuery = ({ source, format, range }) => {
    const mapping = EXPORT_TYPES[source];

    let query =
      `export_type=${mapping.exportType}` +
      `&file_format=${format || mapping.fileFormat}` +
      `&device_id=${device.id}`;

    if (locale) {
      query += `&locale=${locale}`;
    }

    if (range.type === "custom") {
      if (isValid(range.from)) {
        query += `&from=${formatAndEncode(range.from, startOfDay)}`;
      }
      if (isValid(range.to)) {
        query += `&to=${formatAndEncode(range.to, endOfDay)}`;
      }
    } else {
      // show current year data, from the start of the year
      // to the end of the requested day
      const now = new Date();
      const fromVal = formatAndEncode(now, startOfYear);
      const toVal = formatAndEncode(now, endOfDay);

      query += `&from=${fromVal}&to=${toVal}`;
    }

    return query;
  };

  // extended_consumption PDF exports are processed asynchronously and
  // emailed to the user, so they go through the API client instead of a
  // plain navigation, so we can react to the 202/400 responses below.
  const requestPdfExport = query => {
    api
      .get(`/v2/devices/export/?${query}`)
      .then(() => setPdfQueuedDialogOpen(true))
      .catch(e => {
        const errors = e.response && e.response.data;
        const error = Array.isArray(errors) && errors[0];
        const missingContactEmail =
          e.response &&
          e.response.status === 400 &&
          error &&
          error.code === "required" &&
          error.path &&
          error.path[0] === "contact_email";

        if (missingContactEmail) {
          setPendingPdfQuery(query);
          setEmailDialogOpen(true);
        }
      });
  };

  const handleExport = ({ source, format, range }) => {
    const resolvedFormat = format || EXPORT_TYPES[source].fileFormat;
    const query = buildQuery({ source, format, range });

    if (resolvedFormat === "pdf") {
      requestPdfExport(query);
      return;
    }

    window.location.href = `${config.api.baseUrl}/v2/devices/export/?${query}`;
  };

  const handleEmailDialogClose = emailSaved => {
    setEmailDialogOpen(false);

    if (emailSaved && pendingPdfQuery) {
      requestPdfExport(pendingPdfQuery);
    }

    setPendingPdfQuery(null);
  };

  return (
    <>
      <ExportPanel currentView={currentView} onExport={handleExport} />
      {emailDialogOpen && (
        <ExportEmailDialog onClose={handleEmailDialogClose} />
      )}
      {pdfQueuedDialogOpen && (
        <ExportPdfQueuedDialog onClose={() => setPdfQueuedDialogOpen(false)} />
      )}
    </>
  );
};

export default ExportButtonContainer;
